import { throttle } from 'lodash-es';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

/** Same used by [react-native-netinfo](https://github.com/react-native-netinfo/react-native-netinfo?tab=readme-ov-file#netinfoconfiguration) */
const CONNECTION_POLLING_URL = 'https://clients3.google.com/generate_204';
const CONNECTION_POLLING_TIMEOUT = 15_000; // ms
const POLLING_INTERVAL = 60_000; // ms

export interface ConnectivityContext {
    connected: boolean;
    checkInternetReachability: (abort?: AbortController) => Promise<boolean>;
}

const connectivityContext = createContext<ConnectivityContext>({
    connected: true,
    checkInternetReachability: () => Promise.resolve(true),
});

export const ConnectivityProvider = ({ children }: React.PropsWithChildren) => {
    const [connected, setConnected] = useState(navigator.onLine);

    const checkInternetReachability = useCallback(async (abort?: AbortController) => {
        if (!navigator.onLine) {
            return Promise.resolve(false);
        }

        const $abort = abort ?? new AbortController();

        const timeout = setTimeout(() => $abort.abort(), CONNECTION_POLLING_TIMEOUT);

        try {
            await fetch(CONNECTION_POLLING_URL, {
                signal: $abort.signal,
                method: 'HEAD',
                mode: 'no-cors',
            });
            return true;
        } catch {
            return false;
        } finally {
            clearTimeout(timeout);
        }
    }, []);

    useEffect(() => {
        let unmounted = false;
        const throttledCheckInternetReachability = throttle(() => {
            throttledCheckInternetReachability.cancel();
        
            const abort = new AbortController();

            checkInternetReachability(abort)
                .then(isConnected => {
                    if (unmounted) return;
                    if (!isConnected) 
                        console.log('offline');
                    setConnected(isConnected);
                });
        
            return () => {
                unmounted = false;
                abort.abort();
            };
        }, 10_000);

        window.addEventListener('offline', throttledCheckInternetReachability);
        window.addEventListener('online', throttledCheckInternetReachability);

        const pollingInterval = setInterval(throttledCheckInternetReachability, POLLING_INTERVAL);
        
        const cleanup = throttledCheckInternetReachability();
        return () => {
            window.removeEventListener('offline', throttledCheckInternetReachability);
            window.removeEventListener('online', throttledCheckInternetReachability);
            clearInterval(pollingInterval);
            cleanup?.();
        };
    }, [checkInternetReachability]);

    return (
        <connectivityContext.Provider value={{ connected, checkInternetReachability }}>
            {children}
        </connectivityContext.Provider>
    );
};

export const useConnectivity = () => useContext(connectivityContext);
